/**
 * Vowed iPhone Gate PWA — Face ID (WebAuthn user verification) bound to the approval.
 *
 * SEC-CB-020 / J-3529. The previous gate asserted a passkey over a random page-made challenge,
 * discarded the result, re-registered a passkey on any error (a user cancel included) and finally
 * fell back to a browser dialog, so Face ID was cosmetic. This module fixes the phone half:
 *
 *   - The assertion challenge is derived from the EXACT verdict bytes the gate is about to sign
 *     (`approvalChallenge`), so an assertion is specific to one approval of one request.
 *   - `assertFaceId` requires user verification, pins the enrolled credential id, and fails
 *     CLOSED: no passkey, a cancel, a timeout, an error or a malformed response all return
 *     `ok: false`. It never creates, replaces or deletes a passkey.
 *   - `setUpFaceId` is the only function that creates a passkey, and it runs only from the
 *     explicit "Set up Face ID" button. It pins the new passkey only when the provider reports
 *     user verification in the registration (SEC-CB-021).
 *
 * The daemon does not yet verify the assertion (the remaining half of SEC-CB-020). The challenge
 * construction is chosen so that it can: the daemon already builds `verdictSigningBytes` for the
 * verdict it receives and can recompute SHA-256("vowed-gate-approve:v1" || 0x00 || those bytes).
 *
 * The browser APIs are injected through `WebAuthnEnv` so this runs unchanged in Node tests.
 */

import { verdictSigningBytes, type SignedVerdictFields } from "./gate-sign.js";

/** Domain tag for an approval challenge. Changing it breaks daemon-side assertion verification. */
export const APPROVE_CHALLENGE_TAG = "vowed-gate-approve:v1";
/** Domain tag for the first-enrollment challenge. */
export const ENROLL_CHALLENGE_TAG = "vowed-gate-enroll:v1";

/** authenticatorData flags byte (offset 32): bit 0 = User Present, bit 2 = User Verified. */
const FLAG_UP = 0x01;
const FLAG_UV = 0x04;
/** rpIdHash (32) + flags (1) + signCount (4): the fixed prefix of every authenticatorData. */
const AUTH_DATA_MIN = 37;

async function sha256(parts: Uint8Array[]): Promise<Uint8Array> {
  const total = parts.reduce((n, p) => n + p.length, 0);
  const buf = new Uint8Array(total);
  let o = 0;
  for (const p of parts) {
    buf.set(p, o);
    o += p.length;
  }
  return new Uint8Array(await crypto.subtle.digest("SHA-256", buf));
}

/**
 * The WebAuthn challenge for approving a verdict: SHA-256(tag || 0x00 || verdictSigningBytes).
 * `fields` must be the exact object that is then signed; any change to any signed field (request,
 * decision, descriptor hash, code, nonce, gate id, target, expiry, op) changes the challenge.
 */
export async function approvalChallenge(fields: SignedVerdictFields): Promise<Uint8Array> {
  const enc = new TextEncoder();
  return sha256([enc.encode(APPROVE_CHALLENGE_TAG), new Uint8Array([0]), new Uint8Array(verdictSigningBytes(fields))]);
}

/**
 * The WebAuthn challenge for a first enrollment: SHA-256(tag \0 gate_id \0 pubkey \0 nonce), the
 * identity the gate is about to prove possession for with `signEnrollNonce`.
 */
export async function enrollChallenge(gateId: string, pubkeyB64: string, nonce: string): Promise<Uint8Array> {
  const enc = new TextEncoder();
  const nul = new Uint8Array([0]);
  return sha256([enc.encode(ENROLL_CHALLENGE_TAG), nul, enc.encode(gateId), nul, enc.encode(pubkeyB64), nul, enc.encode(nonce)]);
}

/** Why Face ID did not pass. Every one of these means: sign nothing. */
export type FaceIdFailure = "no-passkey" | "cancelled" | "failed";
export type FaceIdResult = { ok: true } | { ok: false; reason: FaceIdFailure };
/**
 * `not-verified`: the provider created a passkey but its authenticatorData lacks the User Present
 * or User Verified flag (e.g. a third-party passkey manager that skips Face ID). It is not pinned.
 */
export type SetupFailure = "cancelled" | "failed" | "not-verified";
export type SetupResult = { ok: true } | { ok: false; reason: SetupFailure };

/** The browser surface this module needs; injected so tests can drive every failure mode. */
export interface WebAuthnEnv {
  rpId: string;
  /** The enrolled Face ID credential id, or undefined when none has been set up. */
  loadCredentialId(): Promise<ArrayBuffer | undefined>;
  saveCredentialId(id: ArrayBuffer): Promise<void>;
  get(options: CredentialRequestOptions): Promise<Credential | null>;
  create(options: CredentialCreationOptions): Promise<Credential | null>;
}

function isCancel(e: unknown): boolean {
  const name = (e as { name?: unknown } | null)?.name;
  return name === "NotAllowedError" || name === "AbortError";
}

function toBytes(b: ArrayBuffer | ArrayBufferView | undefined | null): Uint8Array | null {
  if (!b) return null;
  if (b instanceof ArrayBuffer) return new Uint8Array(b);
  if (ArrayBuffer.isView(b)) return new Uint8Array(b.buffer, b.byteOffset, b.byteLength);
  return null;
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  let d = 0;
  for (let i = 0; i < a.length; i++) d |= a[i] ^ b[i];
  return d === 0;
}

function b64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/**
 * Local sanity checks on the assertion the authenticator returned. The signature itself is not
 * verified here (that is the daemon half), but a response that is not a user-verified assertion
 * over THIS challenge from THIS credential is refused.
 */
function assertionMatches(cred: Credential | null, challenge: Uint8Array, credId: Uint8Array): boolean {
  const pkc = cred as (Credential & {
    rawId?: ArrayBuffer;
    response?: { clientDataJSON?: ArrayBuffer; authenticatorData?: ArrayBuffer };
  }) | null;
  if (!pkc || pkc.type !== "public-key") return false;
  const rawId = toBytes(pkc.rawId);
  if (!rawId || !equalBytes(rawId, credId)) return false;

  const cdj = toBytes(pkc.response?.clientDataJSON);
  if (!cdj) return false;
  let clientData: { type?: unknown; challenge?: unknown };
  try {
    clientData = JSON.parse(new TextDecoder().decode(cdj));
  } catch {
    return false;
  }
  if (clientData.type !== "webauthn.get") return false;
  if (clientData.challenge !== b64url(challenge)) return false;

  const authData = toBytes(pkc.response?.authenticatorData);
  if (!authData || authData.length < AUTH_DATA_MIN) return false;
  return (authData[32] & FLAG_UV) === FLAG_UV;
}

/**
 * Pull the `authData` byte string out of a CBOR attestationObject ({fmt, attStmt, authData}).
 * Deliberately minimal: definite-length items only (CTAP2 CBOR never uses indefinite lengths),
 * bounded nesting, and null on anything unexpected, so a malformed object fails setup closed.
 */
function attestationAuthData(buf: Uint8Array): Uint8Array | null {
  let p = 0;
  const head = (): { major: number; n: number } | null => {
    if (p >= buf.length) return null;
    const ib = buf[p++];
    const major = ib >> 5;
    const ai = ib & 0x1f;
    if (ai < 24) return { major, n: ai };
    const size = ai === 24 ? 1 : ai === 25 ? 2 : ai === 26 ? 4 : ai === 27 ? 8 : 0;
    if (size === 0 || p + size > buf.length) return null; // reserved or indefinite length
    let n = 0;
    for (let i = 0; i < size; i++) n = n * 256 + buf[p + i];
    p += size;
    return Number.isSafeInteger(n) ? { major, n } : null;
  };
  const skip = (depth: number): boolean => {
    if (depth > 8) return false;
    const h = head();
    if (!h) return false;
    switch (h.major) {
      case 0: // unsigned int
      case 1: // negative int
      case 7: // simple / float: head() already consumed its payload
        return true;
      case 2: // byte string
      case 3: // text string
        if (h.n > buf.length - p) return false;
        p += h.n;
        return true;
      case 4: // array
      case 5: // map
        for (let i = 0; i < (h.major === 5 ? 2 * h.n : h.n); i++) if (!skip(depth + 1)) return false;
        return true;
      default: // 6: tag
        return skip(depth + 1);
    }
  };

  const top = head();
  if (!top || top.major !== 5) return null;
  for (let i = 0; i < top.n; i++) {
    const keyAt = p;
    const k = head();
    if (!k) return null;
    let key: string | null = null;
    if (k.major === 3) {
      if (k.n > buf.length - p) return null;
      key = new TextDecoder().decode(buf.subarray(p, p + k.n));
      p += k.n;
    } else {
      p = keyAt;
      if (!skip(0)) return null;
    }
    if (key === "authData") {
      const v = head();
      if (!v || v.major !== 2 || v.n > buf.length - p) return null;
      return buf.slice(p, p + v.n);
    }
    if (!skip(0)) return null;
  }
  return null;
}

/**
 * The authenticatorData of a registration: from the browser's own parse
 * (`response.getAuthenticatorData()`) where available, otherwise from the CBOR attestationObject.
 */
function registrationAuthData(cred: Credential | null): Uint8Array | null {
  const response = (cred as (Credential & {
    response?: { attestationObject?: ArrayBuffer; getAuthenticatorData?: () => ArrayBuffer };
  }) | null)?.response;
  if (!response) return null;
  if (typeof response.getAuthenticatorData === "function") {
    try {
      const fromBrowser = toBytes(response.getAuthenticatorData());
      if (fromBrowser && fromBrowser.length >= AUTH_DATA_MIN) return fromBrowser;
    } catch {
      // fall through to the attestationObject
    }
  }
  const att = toBytes(response.attestationObject);
  const authData = att ? attestationAuthData(att) : null;
  return authData && authData.length >= AUTH_DATA_MIN ? authData : null;
}

/**
 * Face ID for one specific approval. Returns ok only for a user-verified assertion by the enrolled
 * credential over `challenge`. Fails closed on everything else and has no side effects: it never
 * registers a passkey, never replaces the stored id and never offers a manual fallback.
 */
export async function assertFaceId(challenge: Uint8Array, env: WebAuthnEnv): Promise<FaceIdResult> {
  const stored = await env.loadCredentialId();
  const credId = toBytes(stored);
  if (!stored || !credId || credId.length === 0) return { ok: false, reason: "no-passkey" };

  let cred: Credential | null;
  try {
    cred = await env.get({
      publicKey: {
        challenge: new Uint8Array(challenge),
        allowCredentials: [{ id: stored, type: "public-key", transports: ["internal"] }],
        userVerification: "required",
        rpId: env.rpId,
        timeout: 60_000,
      },
    });
  } catch (e) {
    return { ok: false, reason: isCancel(e) ? "cancelled" : "failed" };
  }
  return assertionMatches(cred, challenge, credId) ? { ok: true } : { ok: false, reason: "failed" };
}

/**
 * The explicit "Set up Face ID" action: create a platform (Face ID) passkey and make it the one
 * approvals are pinned to. The previous passkey id is replaced only after a new one is created
 * AND its registration authenticatorData carries both User Present and User Verified (SEC-CB-021),
 * so a cancelled, failed or unverified setup leaves the gate exactly as it was. A provider that
 * creates without user verification would otherwise be pinned and then fail every approval.
 */
export async function setUpFaceId(env: WebAuthnEnv): Promise<SetupResult> {
  let cred: Credential | null;
  try {
    cred = await env.create({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        rp: { id: env.rpId, name: "Vowed Gate" },
        user: { id: crypto.getRandomValues(new Uint8Array(16)), name: "vowed-gate", displayName: "Vowed Gate" },
        pubKeyCredParams: [
          { type: "public-key", alg: -7 },
          { type: "public-key", alg: -257 },
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform", // local biometric, not a cross-device key
          userVerification: "required",
          residentKey: "preferred",
        },
        timeout: 60_000,
      },
    });
  } catch (e) {
    return { ok: false, reason: isCancel(e) ? "cancelled" : "failed" };
  }
  const rawId = (cred as (Credential & { rawId?: ArrayBuffer }) | null)?.rawId;
  if (!cred || !rawId || rawId.byteLength === 0) return { ok: false, reason: "failed" };
  const authData = registrationAuthData(cred);
  if (!authData) return { ok: false, reason: "failed" };
  const required = FLAG_UP | FLAG_UV;
  if ((authData[32] & required) !== required) return { ok: false, reason: "not-verified" };
  await env.saveCredentialId(rawId);
  return { ok: true };
}
